export type ConfiguratorMutationPolicy = "require-success" | "supersede";

export type ConfiguratorMutationResult<T> =
  | { status: "success"; value: T }
  | { status: "failed"; error: unknown }
  | { status: "blocked" };

interface PendingDebouncedMutation {
  generation: number;
  operation: () => Promise<unknown>;
}

/**
 * Owns the single in-flight configurator mutation and pending-input debounce.
 *
 * This is a mutex around the existing request boundary, not a command queue:
 * one caller may drain the pending debounced input, while direct competing
 * callers are blocked instead of retained for later execution. Explicit resets
 * use "supersede" so they may repair state after the pending mutation failed.
 */
export class ConfiguratorMutationCoordinator {
  private activeMutation: Promise<ConfiguratorMutationResult<unknown>> | undefined;
  private latestMutationSucceeded = true;
  private configurationScope: string | undefined;
  private pendingDebouncedMutation: PendingDebouncedMutation | undefined;
  private pendingDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private pendingFollowupInProgress = false;
  private debounceGeneration = 0;

  scheduleDebounced(operation: () => Promise<unknown>, delay: number): void {
    const generation = ++this.debounceGeneration;
    this.pendingDebouncedMutation = { generation, operation };
    if (this.pendingDebounceTimer !== undefined) {
      clearTimeout(this.pendingDebounceTimer);
    }
    this.pendingDebounceTimer = setTimeout(() => {
      if (this.pendingDebouncedMutation?.generation === generation) {
        this.pendingDebounceTimer = undefined;
        void this.settlePendingMutation();
      }
    }, delay);
  }

  run<T>(
    operation: () => Promise<T>,
    policy: ConfiguratorMutationPolicy = "require-success",
  ): Promise<ConfiguratorMutationResult<T>> {
    this.clearPendingDebounceTimer();

    if (this.activeMutation !== undefined || this.pendingFollowupInProgress) {
      return Promise.resolve({ status: "blocked" });
    }

    if (this.pendingDebouncedMutation !== undefined) {
      const configurationScope = this.configurationScope;
      this.pendingFollowupInProgress = true;
      return this.runAfterPendingDebounce(operation, policy, configurationScope);
    }

    if (policy === "require-success" && !this.latestMutationSucceeded) {
      return Promise.resolve({ status: "blocked" });
    }
    return this.start(operation);
  }

  flushAndSettle(): Promise<boolean> {
    this.clearPendingDebounceTimer();
    return this.settlePendingMutation();
  }

  beginConfigurationScope(configId: string): void {
    if (this.configurationScope === configId) {
      return;
    }

    this.configurationScope = configId;
    this.latestMutationSucceeded = true;
    this.pendingDebouncedMutation = undefined;
    this.clearPendingDebounceTimer();
  }

  private async runAfterPendingDebounce<T>(
    operation: () => Promise<T>,
    policy: ConfiguratorMutationPolicy,
    configurationScope: string | undefined,
  ): Promise<ConfiguratorMutationResult<T>> {
    try {
      const pendingMutationSucceeded = await this.settlePendingMutation();
      if (this.configurationScope !== configurationScope) {
        return { status: "blocked" };
      }
      if (policy === "require-success" && !pendingMutationSucceeded) {
        return { status: "blocked" };
      }
      if (this.activeMutation !== undefined || this.pendingDebouncedMutation !== undefined) {
        return { status: "blocked" };
      }
      return await this.start(operation);
    } finally {
      this.pendingFollowupInProgress = false;
    }
  }

  private async settlePendingMutation(): Promise<boolean> {
    while (true) {
      const activeMutation = this.activeMutation;
      if (activeMutation !== undefined) {
        await activeMutation;
        continue;
      }

      const pendingMutation = this.takePendingDebouncedMutation();
      if (pendingMutation !== undefined) {
        await this.start(pendingMutation.operation);
        continue;
      }

      return this.latestMutationSucceeded;
    }
  }

  private takePendingDebouncedMutation(): PendingDebouncedMutation | undefined {
    const pending = this.pendingDebouncedMutation;
    if (pending === undefined) {
      return undefined;
    }

    this.pendingDebouncedMutation = undefined;
    this.clearPendingDebounceTimer();
    return pending;
  }

  private start<T>(operation: () => Promise<T>): Promise<ConfiguratorMutationResult<T>> {
    const mutationScope = this.configurationScope;
    const result = (async (): Promise<ConfiguratorMutationResult<T>> => {
      try {
        const value = await operation();
        if (this.configurationScope === mutationScope) {
          this.latestMutationSucceeded = true;
        }
        return { status: "success", value };
      } catch (error) {
        if (this.configurationScope === mutationScope) {
          this.latestMutationSucceeded = false;
        }
        return { status: "failed", error };
      }
    })();
    this.activeMutation = result;
    void result.then(() => {
      if (this.activeMutation === result) {
        this.activeMutation = undefined;
      }
    });
    return result;
  }

  private clearPendingDebounceTimer(): void {
    if (this.pendingDebounceTimer !== undefined) {
      clearTimeout(this.pendingDebounceTimer);
      this.pendingDebounceTimer = undefined;
    }
  }
}

export const configuratorMutationCoordinator = new ConfiguratorMutationCoordinator();
